class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.header = document.querySelector('.header__logo-wrapperSmall');
    this.closeBtn  = this.querySelector('.search-modal__close-button');
    this.overlayClose = this.querySelector('.modal-overlay');

    this.detailsContainer.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.close();
    });

    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));

    // Close only on a pointerdown OUTSIDE the modal, checked synchronously at
    // the moment of interaction (covers mouse, touch, and pen alike) - this
    // is what actually decides closing now; see onFocusOut for why blur
    // timing alone can't be trusted here. Capture phase so a descendant
    // (e.g. the results' product-grid swiper, which stopPropagation()s its
    // own drag events) can't hide the interaction from this listener.
    document.addEventListener('pointerdown', (e) => {
      if (!this.isOpen() || this.contains(e.target)) return;
      if (this._isExternalOverlayInteraction(e.target)) return;
      this.close();
    }, { capture: true });

    this.summaryToggle.setAttribute('role', 'button');
    this.attechEventListner();
  }

  attechEventListner(){
    this.closeBtn.addEventListener('click', function(){
      this.close();
    }.bind(this));

    this.overlayClose.addEventListener('click', function(){
      this.close();
    }.bind(this));
  }


  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  getResultsMaxHeight() {
    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    return window.innerHeight - headerBottom;
  }

  onSummaryClick(event) {
    event.preventDefault();
    const isOpen = event.target.closest('details').hasAttribute('open');
    isOpen ? this.close() : this.open(event);
  }

  onFocusOut(event) {
    if (!this.isOpen()) return;

    // Closing on pointer interaction (mouse drag, touch, tap) is handled
    // entirely by the pointerdown-outside listener in the constructor -
    // synchronous containment check, no timing race with blur/focusout (the
    // old `_swiperDragging` + setTimeout approach here reset its guard only
    // 100ms after the product-grid swiper's own touchEnd callback, which
    // routinely elapsed before the resulting blur actually landed, closing
    // the search mid-drag). This only needs to catch keyboard-driven focus
    // loss (Tab-ing past the last focusable element) - relatedTarget
    // reliably tells us where focus landed for that case, synchronously.
    if (this._keyboardDismissed) return;

    const nextFocus = event.relatedTarget;
    if (!nextFocus || this.contains(nextFocus)) return;
    if (this._isExternalOverlayInteraction(nextFocus)) return;
    this.close(false);
  }

  // Two overlays that add-to-cart from the search results can trigger both live
  // outside this element in the DOM (siblings appended to <body>, not descendants
  // of <details-modal>): <cart-drawer> (moved there in its connectedCallback) and
  // the shared .mobile-size__selector bottom sheet (rendered there in theme.liquid
  // so every product card can reuse one instance). Interacting with either -
  // tapping a size, tapping outside the sheet to dismiss it, or the focus jump
  // <cart-drawer>'s trapFocus() makes when it opens - looks identical to "focus/a
  // click landed outside the search modal" to the listeners below, and would
  // otherwise close the search out from under the user instead of just stacking
  // the overlay on top of it.
  _isExternalOverlayInteraction(target) {
    if (document.body.classList.contains('cart-drawer-open')) return true;
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.contains(target)) return true;

    const sizeSelector = document.querySelector('.mobile-size__selector');
    if (sizeSelector && (sizeSelector.classList.contains('is-open') || sizeSelector.classList.contains('is-closing') || sizeSelector.contains(target))) {
      return true;
    }

    return false;
  }

  open(event) {
    const detailsElement =
    (event?.target && event.target.closest('details')) ||
    (event?.closest && event.closest('details')) ||
    this.detailsContainer;
    if (!detailsElement) return;
    const headerSearch = detailsElement.closest('.header__search-wrapper');
    if (headerSearch) {
      headerSearch.style.setProperty('display', 'flex', 'important');
    }
    const headerHeight = this.header.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    
    detailsElement.setAttribute('open', "");
    detailsElement.setAttribute('aria-expanded', "true");

    const modalContent = this.querySelector('.search-modal.modal__content');
    // Only the max-width: 767px CSS switches this modal to position: fixed
    // (predictive-search.css) - above that it's the normal in-flow/absolute
    // layout, which doesn't need this repaint nudge.
    if (modalContent && window.matchMedia('(max-width: 767px)').matches) {
      const pinTop = () => {
        const announcementBar = document.querySelector('.utility-bar');
        const top = announcementBar ? Math.max(0, announcementBar.getBoundingClientRect().bottom) : 0;
        modalContent.style.top = `${top}px`;
      };
      pinTop();
      requestAnimationFrame(pinTop);
    }

    // `overflow: hidden` on body alone doesn't stop touch scrolling on iOS
    // Safari - once a drag inside the results list reaches the top/bottom of
    // its own scroll range, the page behind the modal starts scrolling too.
    // Locking body with position: fixed (restoring the exact scroll offset on
    // close) is what actually prevents that on real iOS devices. Shared with
    // other overlays (e.g. the mega menu) via window.ScrollLock so closing
    // this modal doesn't drop a lock still held by one of them.
    window.ScrollLock.lock('search-modal');

    document.body.classList.add('overflow-hidden');
    // Remove the mega menu's own lock class while search is layered on top of
    // it (avoids stacking two `overflow: hidden` sources); restore it on
    // close if the mega menu is still open underneath.
    this._hadOverflowHiddenTablet = document.body.classList.contains('overflow-hidden-tablet');
    document.body.classList.remove('overflow-hidden-tablet');

    if (this.querySelector('.search-modal__content')) {
      document.body.classList.add('search-modal-open');
    }

    // Trap focus inside modal
    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );

    // Calculate and apply max-height
    const maxHeightValue = `${this.getResultsMaxHeight()}px`;
    this.resultsMaxHeight = maxHeightValue;

    // Ensure we're selecting the predictive search within the modal
    this.predictiveSearchResults = this.detailsContainer.querySelector('.predictive-search--wrapper');

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.setProperty('max-height', maxHeightValue);
    } else {
      console.warn('Predictive search wrapper not found.');
    }
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.setAttribute('aria-expanded', "false");
    setTimeout(() => {
      this.detailsContainer.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('search-modal-open');

      if (this._hadOverflowHiddenTablet) {
        document.body.classList.add('overflow-hidden-tablet');
        this._hadOverflowHiddenTablet = false;
      }

      window.ScrollLock.unlock('search-modal');
    }, 300);
  }
}

customElements.define('details-modal', DetailsModal);
